import {Box, Collapse, createStyles, Flex, Group, Text, UnstyledButton} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import moment from 'moment';
import React, {useMemo, useState} from 'react';

import {MetadataGridContents} from 'analysis-tool/data-view/metadata-modal/ViewMetadataModal';
import {CollapsibleTriangle} from 'config-migration/shared-ui-components/TriangleToggle';
import {useBenchlingQuery, QueryResultType} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {CenteredSpinner} from 'ui-library/Spinner';
import {readableTime} from 'util/DateUtil';

import type {
  CanvasDefinitionFileStateQuery,
  CanvasDefinitionFileStateQueryVariables,
} from '../backbone/__generated/CanvasDefinitionFileStateQuery';
import type {
  CanvasVersionFileStateQuery,
  CanvasVersionFileStateQueryVariables,
} from '../backbone/__generated/CanvasVersionFileStateQuery';
import {
  CANVAS_DEFINITION_FILE_STATE_QUERY,
  CanvasDefinitionFileStateGraphQL,
} from '../backbone/CanvasDefinitionGraphQL';
import {
  CANVAS_VERSION_FILE_STATE_QUERY,
  CanvasVersionFileStateGraphQL,
} from '../backbone/CanvasVersionGraphQL';
import CanvasPreviewFrame from '../preview/CanvasPreviewFrame';
import useCanvasExpandedPreviewUrl, {
  CanvasExpandedPreviewTarget,
} from '../preview/useCanvasExpandedPreviewUrl';

type CanvasCreator = CanvasDefinitionFileStateGraphQL['creator'] | CanvasVersionFileStateGraphQL['creator'];

const useStyles = createStyles((theme) => ({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
    overflow: 'auto',
  },
  headerRow: {
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  iconTile: {
    alignItems: 'center',
    backgroundColor: theme.colors.blue[8],
    borderRadius: theme.radius.sm,
    color: theme.white,
    display: 'flex',
    height: 40,
    justifyContent: 'center',
    width: 40,
    flexShrink: 0,
  },
  title: {
    fontSize: theme.fontSizes.xl,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  subtitle: {
    color: theme.colors.gray[6],
    fontSize: theme.fontSizes.sm,
  },
  detailsToggle: {
    alignItems: 'center',
    color: theme.colors.blue[6],
    display: 'inline-flex',
    fontSize: theme.fontSizes.sm,
    gap: theme.spacing.xs,
  },
  metadataValue: {
    wordBreak: 'break-all',
  },
  previewSection: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 480,
  },
}));

interface CanvasViewMetadata {
  canvasDefinitionId: string;
  canvasVersionIndex: number | null;
  createdAt: string;
  creator: CanvasCreator;
  description: string | null;
  title: string;
}

function formatCanvasVersionLabel(index: number | null): string {
  return index != null ? `v${index}` : '—';
}

function getCreatorName(creator: CanvasCreator | null | undefined): string | null {
  if (creator == null || isInaccessible(creator)) {
    return null;
  }
  return creator.name;
}

function metadataFromDefinition(canvasDefinition: CanvasDefinitionFileStateGraphQL): CanvasViewMetadata {
  return {
    canvasDefinitionId: canvasDefinition.id,
    canvasVersionIndex: canvasDefinition.latestUploadedVersionIndex ?? null,
    createdAt: canvasDefinition.createdAt,
    creator: canvasDefinition.creator,
    description: canvasDefinition.description ?? null,
    title: canvasDefinition.name,
  };
}

function metadataFromVersion(canvasVersion: CanvasVersionFileStateGraphQL): CanvasViewMetadata {
  return {
    canvasDefinitionId: canvasVersion.definition.id,
    canvasVersionIndex: canvasVersion.versionIndex,
    createdAt: canvasVersion.createdAt,
    creator: canvasVersion.creator,
    description: canvasVersion.definition.description ?? null,
    title: canvasVersion.definition.name,
  };
}

function CanvasExpandedViewContent({
  metadata,
  target,
}: {
  metadata: CanvasViewMetadata;
  target: CanvasExpandedPreviewTarget;
}): JSX.Element {
  const {classes} = useStyles();
  const [refreshToken, setRefreshToken] = useState(0);
  const [detailsOpened, {toggle: toggleDetails}] = useDisclosure(false);
  const {signedUrl, isLoading, errorMessage} = useCanvasExpandedPreviewUrl(target, refreshToken);

  const creatorName = getCreatorName(metadata.creator);
  const headerCreatedTime = moment(metadata.createdAt).format('M/D/YYYY hh:mm A');
  const metadataCreatedTime = readableTime(metadata.createdAt);

  const metadataGridCells = useMemo(
    () => [
      {
        label: 'Description',
        component: metadata.description ?? '—',
      },
      {
        label: 'Canvas ID',
        component: <span className={classes.metadataValue}>{metadata.canvasDefinitionId}</span>,
      },
      {
        label: 'Version',
        component: formatCanvasVersionLabel(metadata.canvasVersionIndex),
      },
      {
        label: 'Created at',
        component: metadataCreatedTime,
      },
    ],
    [
      classes.metadataValue,
      metadata.canvasDefinitionId,
      metadata.canvasVersionIndex,
      metadata.description,
      metadataCreatedTime,
    ]
  );

  return (
    <div className={classes.page} {...getTestProps({component: 'CanvasExpandedView', element: 'root'})}>
      <Flex className={classes.headerRow}>
        <Group align="flex-start" spacing="md" noWrap={true}>
          <div className={classes.iconTile}>
            <BenchlingIcon icon="canvas_definition" />
          </div>
          <div>
            <Text className={classes.title}>{metadata.title}</Text>
            <Text className={classes.subtitle}>
              Created {headerCreatedTime}
              {creatorName != null && (
                <>
                  {' '}
                  · Created by <strong>{creatorName}</strong>
                </>
              )}
            </Text>
          </div>
        </Group>
        <Tooltip text={isLoading ? 'Refreshing preview...' : 'Refresh preview'}>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.SMALL}
            onClick={() => {
              setRefreshToken((token) => token + 1);
            }}
            disabled={isLoading}
            icon="refresh"
            aria-label="Refresh canvas preview"
          />
        </Tooltip>
      </Flex>

      <UnstyledButton
        className={classes.detailsToggle}
        onClick={toggleDetails}
        aria-expanded={detailsOpened}
        {...getTestProps({component: 'CanvasExpandedView', element: 'detailsToggle'})}
      >
        {detailsOpened ? 'Hide details' : 'Show details'}
        <CollapsibleTriangle isCollapsed={!detailsOpened} />
      </UnstyledButton>

      <Collapse in={detailsOpened}>
        <Box pb="md">
          <MetadataGridContents gridCells={metadataGridCells} />
        </Box>
      </Collapse>

      <div className={classes.previewSection}>
        {isLoading ? (
          <CenteredSpinner />
        ) : errorMessage != null ? (
          <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />
        ) : signedUrl != null ? (
          <CanvasPreviewFrame signedUrl={signedUrl} />
        ) : (
          <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load canvas preview." />
        )}
      </div>
    </div>
  );
}

export default function CanvasExpandedView(props: {
  canvasDefinitionId: string;
  canvasVersionId?: string;
}): JSX.Element {
  if (props.canvasVersionId != null) {
    return <CanvasVersionExpandedView canvasVersionId={props.canvasVersionId} />;
  }
  return <CanvasDefinitionExpandedView canvasDefinitionId={props.canvasDefinitionId} />;
}

function CanvasDefinitionExpandedView({canvasDefinitionId}: {canvasDefinitionId: string}): JSX.Element {
  const queryResult = useBenchlingQuery<
    CanvasDefinitionFileStateQuery,
    CanvasDefinitionFileStateQueryVariables
  >(CANVAS_DEFINITION_FILE_STATE_QUERY, {
    variables: {canvasDefinitionId},
  });

  if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  }

  if (queryResult.type === QueryResultType.ERROR) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load canvas details." />;
  }

  const {canvasDefinition} = queryResult.data;
  if (isInaccessible(canvasDefinition)) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load canvas details." />;
  }

  return (
    <CanvasExpandedViewContent
      metadata={metadataFromDefinition(canvasDefinition)}
      target={{canvasDefinitionId}}
    />
  );
}

function CanvasVersionExpandedView({canvasVersionId}: {canvasVersionId: string}): JSX.Element {
  const queryResult = useBenchlingQuery<CanvasVersionFileStateQuery, CanvasVersionFileStateQueryVariables>(
    CANVAS_VERSION_FILE_STATE_QUERY,
    {
      variables: {canvasVersionId},
    }
  );

  if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  }

  if (queryResult.type === QueryResultType.ERROR) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load canvas details." />;
  }

  const {canvasVersion} = queryResult.data;
  if (isInaccessible(canvasVersion)) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load canvas details." />;
  }

  return (
    <CanvasExpandedViewContent metadata={metadataFromVersion(canvasVersion)} target={{canvasVersionId}} />
  );
}
